import { faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, ReactNode } from 'react';
import { OperatorRequest } from 'src/apis/contract-management/use-operator-by-tso';
import { Tso, AuthenticatedMonolithUser } from 'src/apis/monolith/types';
import { OrganisationAssignmentsRequest } from 'src/apis/monolith/use-organisation-assignments';
import { UserAssignmentRequest } from 'src/apis/monolith/use-user-assignment';
import { TsoRegistrationContact } from 'src/apis/shipper-registration/types';
import { Card } from 'src/components/data-display/card';
import { Divider } from 'src/components/dividers';
import { TsoDocumentList } from 'src/components/domain-specifics/tso-document-list';
import { Stack } from 'src/components/layout/stack';
import { ExternalLink, Link } from 'src/components/navigation/link';
import {
  PageSpinner,
  SpinnerContainer,
} from 'src/components/spinner-container';
import { Successful } from 'src/hooks/use-axios';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { UsePortal } from 'src/hooks/use-portal';
import { useTitle } from 'src/hooks/use-title';
import { TsoLogo } from 'src/pages/market-information/players/operators/tso-logo';
import { AssignmentStatus } from 'src/pages/market-information/players/operators-detail/AssignmentStatus';
import {
  DirectFcFsTable,
  useDirectFcfsInfoTable,
} from 'src/pages/market-information/players/operators-detail/direct-fcfs-table';
import { TsoRequest } from 'src/pages/market-information/players/operators-detail/use-tso';
import { AssignmentDocumentsRequest } from 'src/pages/market-information/players/operators-detail/use-tso-assignment-documents';
import { useTsoRegistrationContact } from 'src/pages/market-information/players/operators-detail/use-tso-registration-contact';
import { Country } from 'src/pages/settings/registration/components/country';
import { Spacer } from 'src/pages/settings/registration/components/form-utils';
import {
  OptionalShipperRegistrationRequest,
  ShipperRegistrationPublicRequest,
} from 'src/pages/shippers/useShipperRegistration';

const RegistrationDataStack: FC<{ children: ReactNode }> = ({ children }) => {
  const { minTablet } = useBreakpoints();
  return (
    <>
      <Spacer />
      <Stack
        templateColumns={minTablet ? '1fr 1fr 1fr' : '1fr'}
        gap={1.5}
        alignItems="start"
      >
        {children}
      </Stack>
      <Spacer />
    </>
  );
};

export const ContactArea: FC<{
  tso: Tso;
}> = ({ tso }) => {
  if (!tso.contact) return null;

  return (
    <>
      <Divider />
      <RegistrationDataStack>
        <strong>Contact Person</strong>

        <>
          <p>
            {tso.contact.firstName || tso.contact.lastName ? (
              <strong>
                {tso.contact.firstName} {tso.contact.lastName}
              </strong>
            ) : null}
          </p>

          {tso.contact.communication ? (
            <div>
              {tso.contact.communication.phone && (
                <p>
                  Phone:{' '}
                  <ExternalLink href={`tel:${tso.contact.communication.phone}`}>
                    {tso.contact.communication.phone}
                  </ExternalLink>
                </p>
              )}

              {tso.contact.communication.fax && (
                <p>Fax: {tso.contact.communication.fax}</p>
              )}

              {tso.contact.communication.email && (
                <p>
                  Email:{' '}
                  <ExternalLink
                    href={`mailto:${tso.contact.communication.email}`}
                  >
                    {tso.contact.communication.email}
                  </ExternalLink>
                </p>
              )}

              {tso.contactFormUrl && (
                <p>
                  <ExternalLink href={tso.contactFormUrl}>
                    Contact Form
                  </ExternalLink>
                </p>
              )}
            </div>
          ) : null}
        </>
      </RegistrationDataStack>
    </>
  );
};

export const RegistrationDataArea: FC<{
  tsoRegistrationContact: TsoRegistrationContact;
}> = ({ tsoRegistrationContact }) => {
  const organisation = tsoRegistrationContact.organisation;
  const userManagement =
    tsoRegistrationContact.userManagement ??
    tsoRegistrationContact.organisation;

  return (
    <>
      <Divider />

      <RegistrationDataStack>
        <strong>Organisation Registration</strong>

        <p>
          <strong>{organisation.team}</strong>
          <br />
          {organisation.type === 'INDIVIDUAL' && (
            <>
              <strong>
                {organisation.gender === 'm' ? 'Mr' : 'Ms/Mrs'}{' '}
                {organisation.givenName} {organisation.surName}
              </strong>
              <br />
            </>
          )}
          {organisation.address.addressLine1}
          <br />
          {organisation.address.addressLine2 && (
            <>
              {organisation.address.addressLine2}
              <br />
            </>
          )}
          {organisation.address.postCode} {organisation.address.city}
          <br />
          <Country code={organisation.address.country?.value!} />
        </p>

        <div>
          <p>
            Phone:{' '}
            <ExternalLink
              href={`tel:${
                organisation.phone.countryCallingCode +
                organisation.phone.subscriber
              }`}
            >
              {organisation.phone.countryCallingCode}{' '}
              {organisation.phone.subscriber}
            </ExternalLink>
          </p>

          {organisation.fax && (
            <p>
              Fax:{' '}
              <ExternalLink
                href={`tel:${
                  organisation.fax.countryCallingCode +
                  organisation.fax.subscriber
                }`}
              >
                {organisation.fax.countryCallingCode}{' '}
                {organisation.fax.subscriber}
              </ExternalLink>
            </p>
          )}

          {organisation.mobile && (
            <p>
              Mobile:{' '}
              <ExternalLink
                href={`tel:${
                  organisation.mobile.countryCallingCode +
                  organisation.mobile.subscriber
                }`}
              >
                {organisation.mobile.countryCallingCode}{' '}
                {organisation.mobile.subscriber}
              </ExternalLink>
            </p>
          )}

          <p>
            Email:{' '}
            <ExternalLink href={`mailto:${organisation.email}`}>
              {organisation.email}
            </ExternalLink>
          </p>

          {organisation.website && (
            <p>
              <ExternalLink href={organisation.website}>
                <FontAwesomeIcon icon={faExternalLink} /> Visit Website
              </ExternalLink>
            </p>
          )}
        </div>
      </RegistrationDataStack>

      <Divider />

      <RegistrationDataStack>
        <strong>User Registration</strong>

        <p>
          <strong>{userManagement.team}</strong>
          <br />
          {userManagement.type === 'INDIVIDUAL' && (
            <>
              <strong>
                {userManagement.gender === 'm' ? 'Mr' : 'Ms/Mrs'}{' '}
                {userManagement.givenName} {userManagement.surName}
              </strong>
              <br />
            </>
          )}
          {userManagement.address.addressLine1}
          <br />
          {userManagement.address.addressLine2 && (
            <>
              {userManagement.address.addressLine2}
              <br />
            </>
          )}
          {userManagement.address.postCode} {userManagement.address.city}
          <br />
          <Country code={userManagement.address.country?.value!} />
        </p>

        <div>
          <p>
            Phone:{' '}
            <ExternalLink
              href={`tel:${
                userManagement.phone.countryCallingCode +
                userManagement.phone.subscriber
              }`}
            >
              {userManagement.phone.countryCallingCode}{' '}
              {userManagement.phone.subscriber}
            </ExternalLink>
          </p>

          {userManagement.fax && (
            <p>
              Fax:{' '}
              <ExternalLink
                href={`tel:${
                  userManagement.fax.countryCallingCode +
                  userManagement.fax.subscriber
                }`}
              >
                {userManagement.fax.countryCallingCode}{' '}
                {userManagement.fax.subscriber}
              </ExternalLink>
            </p>
          )}

          {userManagement.mobile && (
            <p>
              Mobile:{' '}
              <ExternalLink
                href={`tel:${
                  userManagement.mobile.countryCallingCode +
                  userManagement.mobile.subscriber
                }`}
              >
                {userManagement.mobile.countryCallingCode}{' '}
                {userManagement.mobile.subscriber}
              </ExternalLink>
            </p>
          )}

          <p>
            Email:{' '}
            <ExternalLink href={`mailto:${userManagement.email}`}>
              {userManagement.email}
            </ExternalLink>
          </p>

          {userManagement.website && (
            <p>
              <ExternalLink href={userManagement.website}>
                <FontAwesomeIcon icon={faExternalLink} /> Visit Website
              </ExternalLink>
            </p>
          )}
        </div>
      </RegistrationDataStack>
    </>
  );
};

type Props = {
  actionPortal: UsePortal;
  tsoId: string;
  tso: Successful<TsoRequest>;
  shipperRegistration: OptionalShipperRegistrationRequest;
  shipperRegistrationPublic: Successful<ShipperRegistrationPublicRequest>;
  assignmentDocuments: Successful<AssignmentDocumentsRequest>;
  userAssignment?: Successful<UserAssignmentRequest>;
  organisationAssignments?: Successful<OrganisationAssignmentsRequest>;
  caMaOperator?: OperatorRequest;
};

const Page: FC<Props> = ({
  actionPortal,
  tsoId,
  tso,
  shipperRegistration,
  shipperRegistrationPublic,
  assignmentDocuments,
  userAssignment,
  organisationAssignments,
  caMaOperator,
}) => {
  useTitle(`Market Participants - ${tso.response.data.name}`);
  const registrationInformationDocumentUrl =
    shipperRegistration.response?.data._links?.registrationInformationDocument
      ?.href;
  const tsoRegistrationContact = useTsoRegistrationContact({
    tsoId,
    url: shipperRegistrationPublic.response.data._links.tsoRegistrationContact
      .href,
  });

  const infoTableRequest = useDirectFcfsInfoTable(tso.response?.data.eic);

  const monolithUser = useOptionalAuthenticatedMonolithUser();

  const organisationAssignment = organisationAssignments?.response?.data?.find(
    (assignment) => assignment.tso.id === tso.response.data.id
  );

  const tsoResponse = tso.response.data;

  if (
    !(tsoRegistrationContact.response || tsoRegistrationContact.error) ||
    infoTableRequest?.loaded === false
  )
    return <PageSpinner />;

  return (
    <SpinnerContainer pending={tso.pending}>
      {monolithUser?.isShipper &&
        organisationAssignment &&
        actionPortal.renderContent(<LinkToAssignments user={monolithUser} />)}

      <Stack gap={2}>
        <Card>
          <RegistrationDataStack>
            <Stack flow="column" justifyContent="start">
              <TsoLogo
                src={tsoResponse.logoLargeUrl}
                name={tsoResponse.name}
                large
              />
            </Stack>

            <p>
              <strong>{tsoResponse.name}</strong>
              <br />
              {tsoResponse.address.street}
              <br />
              {tsoResponse.address.zipCode} {tsoResponse.address.city}
              <br />
              <Country code={tsoResponse.address.countryCode} />
            </p>
            <div>
              <p>Short Name: {tsoResponse.shortName}</p>
              {tsoResponse.communication ? (
                <>
                  {tsoResponse.communication.phone && (
                    <p>
                      Phone:{' '}
                      <ExternalLink
                        href={`tel:${tsoResponse.communication.phone}`}
                      >
                        {tsoResponse.communication.phone}
                      </ExternalLink>
                    </p>
                  )}

                  {tsoResponse.communication.fax && (
                    <p>Fax: {tsoResponse.communication.fax}</p>
                  )}

                  {tsoResponse.communication.email && (
                    <p>
                      Email:{' '}
                      <ExternalLink
                        href={`mailto:${tsoResponse.communication.email}`}
                      >
                        {tsoResponse.communication.email}
                      </ExternalLink>
                    </p>
                  )}
                </>
              ) : null}

              {tsoResponse.url && (
                <p>
                  <ExternalLink href={tsoResponse.url}>
                    <FontAwesomeIcon icon={faExternalLink} /> Visit Website
                  </ExternalLink>
                </p>
              )}
            </div>
          </RegistrationDataStack>

          {tsoRegistrationContact.response ? (
            <RegistrationDataArea
              tsoRegistrationContact={tsoRegistrationContact.response.data}
            />
          ) : (
            <ContactArea tso={tso.response.data} />
          )}
        </Card>

        {userAssignment && monolithUser && (
          <AssignmentStatus
            userAssignment={userAssignment}
            organisationAssignment={organisationAssignment}
            tso={tso}
            user={monolithUser}
            caMaOperator={caMaOperator}
          />
        )}

        <TsoDocumentList
          tso={tso.response.data}
          assignmentDocuments={assignmentDocuments.response.data}
          registrationInformationDocumentUrl={
            registrationInformationDocumentUrl
          }
        />

        <DirectFcFsTable infoTableRequest={infoTableRequest} />
      </Stack>
    </SpinnerContainer>
  );
};

const LinkToAssignments: FC<{ user: AuthenticatedMonolithUser }> = ({
  user,
}) => {
  const userIsShipperAdmin = user.role === 'SHIPPER_ADMIN';
  if (userIsShipperAdmin) {
    return (
      <div data-testid="assignment-link">
        Go to{' '}
        <Link mode="default-underlined" to="/operators/assignments/overview">
          Assignments
        </Link>{' '}
        to edit your status.
      </div>
    );
  } else {
    return (
      <div data-testid="assignment-link">
        Go to{' '}
        <Link mode="default-underlined" to="/operators/assignments/overview">
          Assignments
        </Link>{' '}
        for further details.
      </div>
    );
  }
};

export default Page;
